import { ReportLineItem, ReportLineItemTypes } from '../../components/report/report.type';
import { getReportColumns } from '../../components/report/ReportColumns';
import { Division } from '../../components/glossary/glossary.type';
import {
  calculateUnitCost,
  calculateCdccCompTotal,
  calculateDeviation,
  calculateDeviationPercentage,
  getNotations,
  getTotalsRow,
  getNotationColor,
  tableRowTypes,
  tableRowsComparator,
} from '../../components/report/report.util';
import { format, formatCurrency, formatPercentage } from 'common/utils/common.util';
import { REPORT_COLORS } from '../../components/report/constants';

export interface CostAnalysisDivisionTableProps {
  division: Division | null;
  lineItems: ReportLineItem[];
  grossBuildingSF: number;
  hardCostSubTotal?: number;
  totalProjectCost?: number;
  name?: string;
}

export function createCostAnalysisDivisionTable({
  division,
  lineItems,
  grossBuildingSF,
  hardCostSubTotal,
  totalProjectCost,
  name,
}: CostAnalysisDivisionTableProps) {
  let normalLineItems = lineItems.filter(row => row.type == null || tableRowTypes.includes(row.type as ReportLineItemTypes)) || [];

  const adjustmentLineItem = lineItems.filter(row => row.type === ReportLineItemTypes.Adjustment)?.[0];
  if (adjustmentLineItem && adjustmentLineItem.cdccCompPerUnit === 0) {
    normalLineItems = normalLineItems.filter(row => row.type !== ReportLineItemTypes.Adjustment);
  }

  const sortedLineItems = [...normalLineItems].sort(tableRowsComparator);
  const isPercentageDivision = ['1.00', '17.00', '18.00'].includes(division?.code ?? '');
  const isAllowances = name === 'ALLOWANCES';
  const colDefs = getReportColumns(null, null, false, false);
  const totalsRow = getTotalsRow(
    `${name ?? division?.name} TOTAL`,
    sortedLineItems,
    grossBuildingSF,
    division?.code || '',
    hardCostSubTotal,
    totalProjectCost,
    lineItems.filter(row => row.type === ReportLineItemTypes.DivisionTotal)[0] || undefined,
  );
  const projectsColColor = ['19.00', '20.00'].includes(division?.code || '') ? REPORT_COLORS.TOTAL_COST_COLOR : REPORT_COLORS.YELLOW;
  const notations = totalsRow?.notation ?? getNotations(totalsRow);

  const bodyRows = [...sortedLineItems]
    .map(lineItem => {
      const isAdjustment = lineItem.type === ReportLineItemTypes.Adjustment;
      const isCompPricing = lineItem.type === ReportLineItemTypes.CompPricing;
      const isAllowance = lineItem.label === 'Allowances';
      const adjustmentColor = isAdjustment ? REPORT_COLORS.ADJUSTMENT_COLOR : isCompPricing ? REPORT_COLORS.COMP_PRICING_COLOR : undefined;
      const bold = isCompPricing;

      // For allowance line items, add a blue tag to the description and make only the total budget blue
      // But don't add the tag if we're in the allowances section (name === 'ALLOWANCES')
      const descriptionContent =
        isAllowance && name !== 'ALLOWANCES'
          ? [
              { text: `${lineItem.lineItem.description ?? '--'} - `, color: adjustmentColor, bold: bold },
              { text: 'Allowance', color: REPORT_COLORS.ALLOWANCE_COLOR, fontSize: 10 },
            ]
          : (lineItem.lineItem.description ?? '--');

      const row = [
        { text: descriptionContent, color: adjustmentColor, bold: bold },
        {
          text: formatCurrency(lineItem.totalBudget.toString(), true),
          color: isAllowance && name !== 'ALLOWANCES' ? REPORT_COLORS.ALLOWANCE_COLOR : adjustmentColor,
          bold: bold,
        },
        { text: format(lineItem.quantity?.toString() || '0', 'number'), color: adjustmentColor, bold: bold },
        { text: lineItem.unit, color: adjustmentColor, bold: bold },
        { text: formatCurrency(calculateUnitCost(lineItem).toString(), true), color: adjustmentColor, bold: bold },
        { text: formatCurrency(lineItem.cdccCompPerUnit.toString(), true), color: adjustmentColor, bold: bold },
        { text: formatCurrency(calculateCdccCompTotal(lineItem).toString(), true), color: adjustmentColor, bold: bold },
        { text: formatCurrency(calculateDeviation(lineItem).toString(), true), color: adjustmentColor, bold: bold },
        { text: '', color: adjustmentColor },
        { text: lineItem?.notation, color: getNotationColor(lineItem?.notation ?? null), bold: bold },
      ];
      if (isAdjustment || isCompPricing) {
        const emptyRow = Array(colDefs.length).fill('');
        return [emptyRow, row];
      }
      return [row];
    })
    .flat(1);

  const table = {
    headerRows: 2,
    heights: 16,
    widths: colDefs.map(col => (['description', 'notations'].includes(col.field) ? 150 : 80)),
    body: [
      [
        { text: name ?? `${division?.code} - ${division?.name}`, colSpan: colDefs.length, alignment: 'left', bold: true, fontSize: 14 },
        ...Array(colDefs.length - 1).fill(''),
      ], // division header
      [...colDefs.map((colDef, index) => ({ text: colDef.headerName, fillColor: index <= 4 ? projectsColColor : REPORT_COLORS.BLUE }))], // col header row
      ...bodyRows,
      [
        { text: totalsRow.lineItem.description, bold: true, fillColor: projectsColColor },
        { text: formatCurrency(totalsRow.totalBudget.toString(), true), fillColor: projectsColColor },
        isPercentageDivision || isAllowances
          ? { text: formatPercentage(totalsRow.quantity.toString(), true), fillColor: projectsColColor }
          : { text: formatCurrency(totalsRow.quantity.toString(), true), fillColor: projectsColColor },
        { text: totalsRow.unit, fillColor: projectsColColor },
        { text: formatCurrency(calculateUnitCost(totalsRow).toString(), true), fillColor: projectsColColor },
        isPercentageDivision
          ? { text: formatPercentage(totalsRow.cdccCompPerUnit.toString(), true), fillColor: REPORT_COLORS.BLUE }
          : { text: formatCurrency(totalsRow.cdccCompPerUnit.toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: formatCurrency(totalsRow.cdccCompTotal.toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: formatCurrency(calculateDeviation(totalsRow).toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: formatPercentage((calculateDeviationPercentage(totalsRow) * 100).toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: notations, color: getNotationColor(notations), fillColor: REPORT_COLORS.BLUE },
      ],
    ],
  };
  return {
    table: table,
    margin: 10,
    layout: 'lightHorizontalLines',
  };
}

export function renderTotalsRow(totalsRow: any, bgColor = REPORT_COLORS.YELLOW) {
  const colDefs = getReportColumns(null, null, false, false);
  const notations = totalsRow?.notation ?? getNotations(totalsRow);
  const table = {
    headerRows: 1,
    widths: colDefs.map(col => (['description', 'notations'].includes(col.field) ? 150 : 80)),
    body: [
      [...colDefs.map((colDef, index) => ({ text: colDef.headerName, fillColor: index <= 4 ? bgColor : REPORT_COLORS.BLUE }))], // col header row
      [
        { text: totalsRow.lineItem.description, bold: true, fillColor: bgColor },
        { text: formatCurrency(totalsRow.totalBudget.toString(), true), fillColor: bgColor },
        { text: formatCurrency(totalsRow.quantity.toString(), true), fillColor: bgColor },
        { text: totalsRow.unit, fillColor: bgColor },
        { text: formatCurrency(calculateUnitCost(totalsRow).toString(), true), fillColor: bgColor },
        { text: formatCurrency(totalsRow.cdccCompPerUnit.toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: formatCurrency(totalsRow.cdccCompTotal.toString(), true), fillColor: REPORT_COLORS.BLUE },
        { text: formatCurrency(calculateDeviation(totalsRow).toString(), true), fillColor: REPORT_COLORS.BLUE },
        {
          text: formatPercentage((calculateDeviationPercentage(totalsRow) * 100).toString(), true),
          fillColor: REPORT_COLORS.BLUE,
        },
        { text: notations, color: getNotationColor(notations), fillColor: REPORT_COLORS.BLUE },
      ],
    ],
  };
  return {
    table: table,
    margin: 10,
    layout: 'noBorders',
  };
}
